import React from 'react';
import { ResultComponentProps } from '../../common/types';
import BaseResultDisplay from '../../common/BaseResultDisplay';
import { Table, Button, message, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

// E.coli表达量预测结果展示组件
const EcoliExpressionResult: React.FC<ResultComponentProps> = ({ 
  data, 
  title, 
  onDeleteResult, 
  multiResults = false,
  resultIndex
}) => {
  const [tableData, setTableData] = React.useState<any[]>([]);

  // 截断序列显示函数
  const truncateSequence = (sequence: string, maxLength: number = 60): string => {
    if (!sequence || sequence.length <= maxLength) {
      return sequence || '';
    }
    return sequence.substring(0, maxLength) + '...';
  };

  React.useEffect(() => {
    try {
      if (data) {
        let parsedData = [];
        console.log('原始数据类型:', typeof data);
        
        // 处理新的统一格式数据
        if (data.results && Array.isArray(data.results)) {
          parsedData = data.results;
          console.log('使用新的统一格式数据 - results数组');
        }
        // 兼容旧的 csv_data 格式
        else if (data.csv_data) {
          console.log('csv_data 类型:', typeof data.csv_data);
          // 如果 csv_data 是字符串，尝试解析为 JSON
          if (typeof data.csv_data === 'string') {
            try {
              // 尝试处理可能的双重转义
              let jsonString = data.csv_data;
              // 如果字符串以引号开始和结束，可能是被额外引用的JSON
              if (jsonString.startsWith('"') && jsonString.endsWith('"')) {
                jsonString = jsonString.slice(1, -1).replace(/\\"/g, '"');
              }
              parsedData = JSON.parse(jsonString);
              console.log('成功解析JSON字符串');
            } catch (e) {
              console.error('解析 csv_data 字符串失败:', e);
              // 尝试直接解析
              try {
                parsedData = JSON.parse(data.csv_data);
                console.log('第二次尝试成功解析JSON字符串');
              } catch (e2) {
                console.error('第二次解析尝试失败:', e2);
                message.error('解析数据失败');
              }
            }
          } 
          // 如果 csv_data 已经是数组
          else if (Array.isArray(data.csv_data)) {
            parsedData = data.csv_data;
            console.log('csv_data 是数组');
          }
          // 如果 csv_data 是对象但不是数组
          else if (typeof data.csv_data === 'object') {
            parsedData = [data.csv_data];
            console.log('csv_data 是对象');
          }
        } 
        // 如果 data 本身是数组
        else if (Array.isArray(data)) {
          parsedData = data;
          console.log('data 是数组');
        }
        
        console.log('解析后的数据:', parsedData);
        
        // 格式化表格数据
        if (Array.isArray(parsedData) && parsedData.length > 0) {
          const formattedData = parsedData.map((item: any, index: number) => {
            // 处理新格式数据
            if (item.sequence_id && item.result) {
              return {
                key: index.toString(),
                id: item.sequence_id,
                sequence: item.sequence || '',
                score: item.result.expression_score || 0,
              };
            }
            // 处理旧格式数据
            else {
              return {
                key: index.toString(),
                id: item.id || `序列${index + 1}`,
                sequence: item.sequence || '',
                score: typeof item.score === 'number' ? item.score : parseFloat(item.score) || 0,
              };
            }
          });
          
          setTableData(formattedData);
          console.log('设置表格数据:', formattedData);
        } else {
          console.warn('解析后的数据不是数组或为空');
          setTableData([]);
        }
      }
    } catch (error) {
      console.error('处理数据时出错:', error);
      message.error('处理数据时出错');
    }
  }, [data]);

  // 导出为CSV
  const handleExportCSV = () => {
    try {
      if (!tableData || tableData.length === 0) {
        message.error('没有可导出的数据');
        return;
      }

      // 表头 - 添加序列列
      const headers = ['ID', '序列', '表达量预测分数'].join(',');
      
      // 行数据 - 包含完整序列
      const rows = tableData.map(item => [
        `"${item.id || ''}"`,
        `"${item.sequence || ''}"`, // 导出完整序列
        (item.score || 0).toFixed(4)
      ].join(','));

      // 创建CSV内容
      const csvContent = `${headers}\n${rows.join('\n')}`;

      // 创建并下载CSV文件
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `E.coli表达量预测结果_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success('CSV文件导出成功');
    } catch (error) {
      console.error('导出CSV文件失败:', error);
      message.error('导出CSV文件失败');
    }
  };

  // 创建表格列定义
  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { 
      title: '序列', 
      dataIndex: 'sequence', 
      key: 'sequence',
      render: (sequence: string) => {
        const truncated = truncateSequence(sequence, 60);
        const isTruncated = sequence && sequence.length > 60;
        
        return isTruncated ? (
          <Tooltip title={sequence} placement="topLeft">
            <span style={{ cursor: 'help' }}>{truncated}</span>
          </Tooltip>
        ) : (
          <span>{truncated}</span>
        );
      }
    },
    { 
      title: '表达量预测分数', 
      dataIndex: 'score', 
      key: 'score', 
      sorter: (a: any, b: any) => b.score - a.score, // 默认降序排列
      defaultSortOrder: 'descend' as const,
      render: (value: number) => value.toFixed(4)
    },
  ];

  return (
    <BaseResultDisplay
      data={data}
      title={title || "E.coli表达量预测结果"}
      onDeleteResult={onDeleteResult}
      showRawData={false}
      multiResults={multiResults}
    >
      <div style={{ marginBottom: 16 }}>
        <Button 
          type="primary" 
          icon={<DownloadOutlined />} 
          onClick={handleExportCSV}
          disabled={!tableData || tableData.length === 0}
        >
          导出CSV
        </Button>
      </div>
      
      <Table 
        columns={columns} 
        dataSource={tableData} 
        pagination={tableData.length > 10 ? { 
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total) => `共 ${total} 条记录`
        } : false} 
        bordered 
        size="middle"
        scroll={{ x: 'max-content' }}
      />
    </BaseResultDisplay>
  );
};

export default EcoliExpressionResult;